<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import reportsAPI from 'dashboard/api/reports';
import summaryReportsAPI from 'dashboard/api/summaryReports';
import csatReportsAPI from 'dashboard/api/csatReports';
import conversationsAPI from 'dashboard/api/conversations';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import {
  fmtDur,
  fmtWait,
  pctDelta,
  slaScore,
  aggregateMeanByHour,
} from './helpers/chatDashboardHelpers';

const { t } = useI18n();
const store = useStore();

// ── Constantes visuais ────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#2163FF', '#00D4E0', '#7B8CFF', '#F5A524', '#19C37D'];
const HEAT_HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
const HEAT_DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
// jsDay(0=Dom..6=Sáb) → índice de display(0=Seg..6=Dom)
const DOW_TO_DISPLAY = [6, 0, 1, 2, 3, 4, 5];
const DOW_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CHANNEL_LABELS = {
  'Channel::WebWidget': 'Chat do site',
  'Channel::Api': 'WhatsApp',
  'Channel::Email': 'E-mail',
  'Channel::Instagram': 'Instagram',
  'Channel::Telegram': 'Telegram',
  'Channel::TwitterProfile': 'Twitter/X',
  'Channel::FacebookPage': 'Facebook',
  'Channel::Sms': 'SMS',
};
const CHANNEL_COLORS = {
  'Channel::WebWidget': '#2163FF',
  'Channel::Api': '#25D366',
  'Channel::Email': '#00D4E0',
  'Channel::Instagram': '#E1306C',
  'Channel::Telegram': '#229ED9',
  'Channel::TwitterProfile': '#1DA1F2',
  'Channel::FacebookPage': '#1877F2',
  'Channel::Sms': '#F5A524',
};

// ── Estado ─────────────────────────────────────────────────────────────────────
const period = ref('semana');
const hourlyView = ref('sint');
const dailyView = ref('sint');
const loading = ref(true);
const customDateRange = ref(null);
const showExport = ref(false);
const slaRisk = ref([]);

// ── Dados brutos da API ───────────────────────────────────────────────────────
const summary = ref(null);
const botSummaryData = ref(null);
const hourlyCountRaw = ref([]);
const dailyCountRaw = ref([]);
const hourlyTmprRaw = ref([]);
const hourlyTmaRaw = ref([]);
const dailyTmprRaw = ref([]);
const dailyTmaRaw = ref([]);
const agentRaw = ref([]);
const channelRaw = ref({});
const csatRaw = ref(null);
const labelRaw = ref([]);
const storeAgentsData = ref([]);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = n => Number(n).toLocaleString('pt-BR');
const pad2 = n => String(n).padStart(2, '0');
const fmtH = i => `${pad2(i)}h`;
const fmtDate = ts => {
  const d = new Date(Number(ts) * 1000);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
};
const initials = nm =>
  nm
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('');

// ── Range de datas ────────────────────────────────────────────────────────────
const getRange = p => {
  if (p === 'personalizado' && customDateRange.value?.[0]) {
    const [start, end] = customDateRange.value;
    return {
      since: Math.floor(start.getTime() / 1000),
      until: Math.floor(end.getTime() / 1000),
    };
  }
  const now = Math.floor(Date.now() / 1000);
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const startTs = Math.floor(d.getTime() / 1000);
  if (p === 'hoje') return { since: startTs, until: now };
  if (p === 'semana') return { since: startTs - 6 * 86400, until: now };
  return { since: startTs - 29 * 86400, until: now };
};

// ── Busca de dados ────────────────────────────────────────────────────────────
const fetchData = async () => {
  loading.value = true;
  const { since, until } = getRange(period.value);

  try {
    const agentsInStore = store.getters['agents/getAgents'];
    if (!agentsInStore || !agentsInStore.length) {
      await store.dispatch('agents/get');
    }
    storeAgentsData.value = store.getters['agents/getAgents'] || [];

    const slaPoliciesInStore = store.getters['sla/getSLA'];
    if (!slaPoliciesInStore || !slaPoliciesInStore.length) {
      await store.dispatch('sla/get');
    }
  } catch (_) {
    storeAgentsData.value = [];
  }

  const [
    summaryRes,
    botRes,
    hourlyCountRes,
    dailyCountRes,
    hourlyTmprRes,
    hourlyTmaRes,
    dailyTmprRes,
    dailyTmaRes,
    agentRes,
    channelRes,
    csatRes,
    labelRes,
  ] = await Promise.allSettled([
    reportsAPI.getSummary(since, until),
    reportsAPI.getBotSummary({ from: since, to: until }),
    reportsAPI.getReports({
      metric: 'conversations_count',
      from: since,
      to: until,
      groupBy: 'hour',
    }),
    reportsAPI.getReports({
      metric: 'conversations_count',
      from: since,
      to: until,
      groupBy: 'day',
    }),
    reportsAPI.getReports({
      metric: 'avg_first_response_time',
      from: since,
      to: until,
      groupBy: 'hour',
    }),
    reportsAPI.getReports({
      metric: 'avg_resolution_time',
      from: since,
      to: until,
      groupBy: 'hour',
    }),
    reportsAPI.getReports({
      metric: 'avg_first_response_time',
      from: since,
      to: until,
      groupBy: 'day',
    }),
    reportsAPI.getReports({
      metric: 'avg_resolution_time',
      from: since,
      to: until,
      groupBy: 'day',
    }),
    summaryReportsAPI.getAgentReports({ since, until }),
    summaryReportsAPI.getChannelReports({ since, until }),
    csatReportsAPI.getMetrics({ from: since, to: until }),
    summaryReportsAPI.getLabelReports({ since, until }),
  ]);

  const ok = r => (r.status === 'fulfilled' ? r.value.data : null);
  summary.value = ok(summaryRes);
  botSummaryData.value = ok(botRes);
  hourlyCountRaw.value = ok(hourlyCountRes) || [];
  dailyCountRaw.value = ok(dailyCountRes) || [];
  hourlyTmprRaw.value = ok(hourlyTmprRes) || [];
  hourlyTmaRaw.value = ok(hourlyTmaRes) || [];
  dailyTmprRaw.value = ok(dailyTmprRes) || [];
  dailyTmaRaw.value = ok(dailyTmaRes) || [];
  agentRaw.value = ok(agentRes) || [];
  channelRaw.value = ok(channelRes) || {};
  csatRaw.value = ok(csatRes);
  labelRaw.value = ok(labelRes) || [];

  try {
    const accountId = window.location.pathname.split('/')[3];
    const slaRes = await conversationsAPI.list({
      status: 'open',
      sort: 'waiting_since',
      page: 1,
    });
    const payload = slaRes.data?.data?.payload || [];
    const nowSec = Math.floor(Date.now() / 1000);
    slaRisk.value = payload
      .filter(c => c.waiting_since && nowSec - c.waiting_since > 300)
      .slice(0, 10)
      .map(c => ({
        id: c.id,
        agent: c.meta?.assignee?.name || t('CHAT_DASHBOARD.SLA_UNASSIGNED'),
        waited: nowSec - (c.waiting_since || c.created_at),
        status: nowSec - c.waiting_since > 600 ? 'breached' : 'risk',
        link: `/app/accounts/${accountId}/conversations/${c.id}`,
      }));
  } catch (_) {
    slaRisk.value = [];
  }

  loading.value = false;
};

onMounted(fetchData);
watch(period, newP => {
  if (newP !== 'personalizado') fetchData();
  else if (customDateRange.value?.[0]) fetchData();
});

// ── KPIs de volume ────────────────────────────────────────────────────────────
const vsLabel = computed(() => {
  if (period.value === 'hoje') return 'vs. ontem';
  if (period.value === 'semana') return 'vs. período ant.';
  if (period.value === 'mes') return 'vs. mês ant.';
  return 'vs. período ant.';
});

const customLabel = computed(() => {
  if (!customDateRange.value?.[0]) return null;
  const [start, end] = customDateRange.value;
  return `${fmtDate(Math.floor(start.getTime() / 1000))} – ${fmtDate(Math.floor(end.getTime() / 1000))}`;
});

const onCustomRangeChanged = newRange => {
  if (!newRange?.[0] || !newRange?.[1]) return;
  customDateRange.value = newRange;
  period.value = 'personalizado';
  fetchData();
};

const convKpi = computed(() => {
  if (!summary.value) return { val: '—', delta: null, dir: '' };
  const cur = summary.value.conversations_count || 0;
  const prev = summary.value.previous?.conversations_count;
  if (!prev) return { val: fmt(cur), delta: null, dir: '' };
  const diff = (((cur - prev) / prev) * 100).toFixed(1);
  const isUp = parseFloat(diff) >= 0;
  return {
    val: fmt(cur),
    delta: `${isUp ? '▲' : '▼'} ${Math.abs(parseFloat(diff)).toFixed(1).replace('.', ',')}%`,
    dir: isUp ? 'up' : 'down',
  };
});

const tmprKpi = computed(() => {
  if (!summary.value?.avg_first_response_time)
    return { val: '—', delta: null, dir: '' };
  const cur = summary.value.avg_first_response_time;
  const prev = summary.value.previous?.avg_first_response_time;
  return { val: fmtDur(cur), ...pctDelta(cur, prev) };
});

const tmaKpi = computed(() => {
  if (!summary.value?.avg_resolution_time)
    return { val: '—', delta: null, dir: '' };
  const cur = summary.value.avg_resolution_time;
  const prev = summary.value.previous?.avg_resolution_time;
  return { val: fmtDur(cur), ...pctDelta(cur, prev) };
});

const queueKpi = computed(() => {
  if (!summary.value?.reply_time)
    return { val: t('CHAT_DASHBOARD.NOT_MONITORED'), delta: null, dir: '' };
  const cur = Number(summary.value.reply_time);
  const prev = Number(summary.value.previous?.reply_time) || 0;
  return { val: fmtDur(cur), ...pctDelta(cur, prev) };
});

const botKpi = computed(() => {
  const bd = botSummaryData.value;
  if (!bd) return { val: t('CHAT_DASHBOARD.NO_DATA'), delta: null, dir: '' };
  const res = bd.bot_resolutions_count || 0;
  const hand = bd.bot_handoffs_count || 0;
  // Flow Builder não dispara conversation_bot_handoff (exclusivo do AgentBot nativo).
  // Quando hand=0, usar resolutions_count como denominador para evitar 100% artificial.
  const total =
    hand > 0 ? res + hand : summary.value?.resolutions_count || 0;
  if (!total)
    return { val: t('CHAT_DASHBOARD.BOT_INACTIVE'), delta: null, dir: '' };
  const pct = Math.round((res / total) * 100);
  const prevRes = bd.previous?.bot_resolutions_count || 0;
  const prevHand = bd.previous?.bot_handoffs_count || 0;
  const prevTotal = prevRes + prevHand;
  if (!prevTotal) return { val: `${pct}%`, delta: null, dir: '' };
  const prevPct = Math.round((prevRes / prevTotal) * 100);
  const diff = (pct - prevPct).toFixed(1);
  const isUp = parseFloat(diff) >= 0;
  return {
    val: `${pct}%`,
    delta: `${isUp ? '▲' : '▼'} ${Math.abs(parseFloat(diff)).toFixed(1).replace('.', ',')} p.p.`,
    dir: isUp ? 'up' : 'down',
  };
});

// ── Hora de pico ──────────────────────────────────────────────────────────────
const peakHour = computed(() => {
  if (!hourlyCountRaw.value.length) return '—';
  const byHour = Array(24).fill(0);
  hourlyCountRaw.value.forEach(({ timestamp, value }) => {
    const h = new Date(Number(timestamp) * 1000).getHours();
    byHour[h] += Number(value) || 0;
  });
  const maxVal = Math.max(...byHour);
  if (!maxVal) return '—';
  return `${pad2(byHour.indexOf(maxVal))}h`;
});

// ── Barras de volume (diárias) ────────────────────────────────────────────────
const volumeBars = computed(() => {
  const data = dailyCountRaw.value;
  if (!data.length) return [];
  const vals = data.map(d => Number(d.value) || 0);
  const maxV = Math.max(...vals) || 1;
  const n = data.length;
  let labelEvery = 5;
  if (n <= 7) labelEvery = 1;
  else if (n <= 14) labelEvery = 2;
  return data.map((d, i) => ({
    label: fmtDate(d.timestamp),
    atd: Number(d.value) || 0,
    inativ: 0,
    hAtdPct: ((Number(d.value) || 0) / maxV) * 100,
    hInativPct: 0,
    showLabel: i % labelEvery === 0 || i === n - 1,
  }));
});

// ── Donut de canais ───────────────────────────────────────────────────────────
const donutSegments = computed(() => {
  const data = channelRaw.value;
  if (!data || !Object.keys(data).length) return [];
  const entries = Object.entries(data)
    .map(([key, val]) => ({
      label: CHANNEL_LABELS[key] || key.replace('Channel::', ''),
      color: CHANNEL_COLORS[key] || '#7B8CFF',
      total: val.total || 0,
    }))
    .filter(e => e.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 6);
  const totalAll = entries.reduce((s, e) => s + e.total, 0) || 1;
  let offset = 25;
  return entries.map(e => {
    const pc = Math.round((e.total / totalAll) * 100);
    const seg = { ...e, pc, offset };
    offset -= pc;
    return seg;
  });
});

// ── Heatmap ───────────────────────────────────────────────────────────────────
const heatmap = computed(() => {
  const grid = Array(7)
    .fill(null)
    .map(() => Array(24).fill(0));
  hourlyCountRaw.value.forEach(({ timestamp, value }) => {
    const d = new Date(Number(timestamp) * 1000);
    const displayDow = DOW_TO_DISPLAY[d.getDay()];
    grid[displayDow][d.getHours()] += Number(value) || 0;
  });
  const maxVal = Math.max(...grid.flat()) || 1;
  const logMax = Math.log1p(maxVal);
  return HEAT_DAYS.map((day, di) =>
    HEAT_HOURS.map(h => ({
      day,
      h,
      val: grid[di][h] || 0,
      opacity: Math.max(0.08, Math.log1p(grid[di][h] || 0) / logMax),
    }))
  );
});

// ── Barras horárias ───────────────────────────────────────────────────────────
const hourByDayOfHour = computed(() => {
  const byHour = Array(24).fill(0);
  hourlyCountRaw.value.forEach(({ timestamp, value }) => {
    byHour[new Date(Number(timestamp) * 1000).getHours()] += Number(value) || 0;
  });
  return byHour;
});

const hourlyBars = computed(() => {
  const h = hourByDayOfHour.value;
  const maxH = Math.max(...h) || 1;
  return h.map((v, i) => ({
    i,
    v,
    pct: (v / maxH) * 100,
    isPeak: v === maxH && v > 0,
    isLow: v > 0 && v <= maxH * 0.12,
    showLabel: [0, 6, 12, 18].includes(i),
    label: pad2(i),
    markLabel: [0, 6, 12, 18].includes(i) ? pad2(i) : null,
  }));
});

const commercialWindow = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  const slots = inboxes
    .filter(i => i.working_hours?.length)
    .flatMap(i => i.working_hours.filter(w => !w.closed_all_day && w.open_hour != null));
  if (!slots.length) return { start: 8, end: 18 };
  return {
    start: Math.min(...slots.map(w => w.open_hour)),
    end: Math.max(...slots.map(w => w.close_hour)),
  };
});

const hourlyStats = computed(() => {
  const h = hourByDayOfHour.value;
  const maxH = Math.max(...h) || 0;
  const peakIdx = h.indexOf(maxH);
  const total = h.reduce((a, b) => a + b, 0) || 1;
  const active = h.filter(v => v > 0);
  const minA = active.length ? Math.min(...active) : 0;
  const valeIdx = h.indexOf(minA);
  const { start, end } = commercialWindow.value;
  const commercial = h.slice(start, end + 1).reduce((a, b) => a + b, 0);
  const outside = total - commercial;
  const sep = t('CHAT_DASHBOARD.H_SEP');
  return {
    peak: `${pad2(peakIdx)}h ${sep} ${fmt(maxH)} conv`,
    vale: `${pad2(valeIdx)}h ${sep} ${fmt(minA)} conv`,
    commercial: `${Math.round((commercial / total) * 100)}% ${sep} ${fmt(commercial)}`,
    outside: `${Math.round((outside / total) * 100)}% ${sep} ${fmt(outside)}`,
    avg: `${fmt(Math.round(total / (active.length || 1)))} conv`,
  };
});

// ── Tabela analítica horária ──────────────────────────────────────────────────
const hourlyAna = computed(() => {
  const counts = hourByDayOfHour.value;
  const tmprByHour = aggregateMeanByHour(hourlyTmprRaw.value);
  const total = counts.reduce((a, b) => a + b, 0) || 1;
  const maxH = Math.max(...counts) || 1;
  const peakIdx = counts.indexOf(maxH);
  const rows = counts
    .map((v, i) => {
      if (!v) return null;
      return {
        label: `${pad2(i)}h – ${pad2(i)}:59`,
        conv: v,
        tmpr: tmprByHour[i] !== null ? fmtDur(tmprByHour[i]) : '—',
        pct: `${((v / total) * 100).toFixed(1).replace('.', ',')}%`,
        isPeak: i === peakIdx,
      };
    })
    .filter(Boolean);
  return {
    rows,
    totals: { conv: rows.reduce((a, r) => a + r.conv, 0) },
  };
});

// ── Série diária ──────────────────────────────────────────────────────────────
const dailySeries = computed(() => {
  const data = dailyCountRaw.value;
  if (!data.length) return [];
  const vals = data.map(d => Number(d.value) || 0);
  const maxV = Math.max(...vals) || 1;
  const total = vals.reduce((a, b) => a + b, 0) || 1;
  let labelEvery;
  if (data.length <= 10) {
    labelEvery = 1;
  } else if (data.length <= 20) {
    labelEvery = 3;
  } else {
    labelEvery = 5;
  }
  return data.map((d, i) => {
    const dt = new Date(Number(d.timestamp) * 1000);
    const v = Number(d.value) || 0;
    return {
      label: fmtDate(d.timestamp),
      dow: dt.getDay(),
      dowStr: DOW_LABELS[dt.getDay()],
      v,
      pct: (v / maxV) * 100,
      pctTotal: ((v / total) * 100).toFixed(1).replace('.', ','),
      isPeak: v === maxV,
      isLow: v > 0 && v <= maxV * 0.12,
      isFds: dt.getDay() === 0 || dt.getDay() === 6,
      showLabel: i % labelEvery === 0 || i === data.length - 1,
    };
  });
});

const dailyStats = computed(() => {
  const s = dailySeries.value;
  if (!s.length)
    return { best: '—', worst: '—', avg: '—', fds: '—', ndias: '0' };
  const vals = s.map(d => d.v);
  const maxV = Math.max(...vals);
  const minV = Math.min(...vals.filter(v => v > 0));
  const total = vals.reduce((a, b) => a + b, 0) || 1;
  const best = s.find(d => d.v === maxV);
  const worst = s.find(d => d.v === minV);
  const fdsList = s.filter(d => d.isFds);
  const fdsTotal = fdsList.reduce((a, d) => a + d.v, 0);
  const sep = t('CHAT_DASHBOARD.H_SEP');
  return {
    best: best ? `${best.label} ${sep} ${fmt(maxV)}` : '—',
    worst: worst ? `${worst.label} ${sep} ${fmt(minV)}` : '—',
    avg: `${fmt(Math.round(total / s.length))} conv`,
    fds: total
      ? `${Math.round((fdsTotal / total) * 100)}% ${sep} ${fmt(fdsTotal)}`
      : '—',
    ndias: String(s.length),
  };
});

// ── Tabela analítica diária ───────────────────────────────────────────────────
const dailyAna = computed(() => {
  const s = dailySeries.value;
  if (!s.length) return { rows: [], totals: { conv: 0 } };
  const tmprByDay = {};
  dailyTmprRaw.value.forEach(({ timestamp, value }) => {
    const key = fmtDate(timestamp);
    const v = Number(value);
    if (v > 0) tmprByDay[key] = v;
  });
  const tmaByDay = {};
  dailyTmaRaw.value.forEach(({ timestamp, value }) => {
    const key = fmtDate(timestamp);
    const v = Number(value);
    if (v > 0) tmaByDay[key] = v;
  });
  const rows = s.map(d => ({
    date: d.label,
    dow: d.dowStr,
    conv: d.v,
    tmpr: tmprByDay[d.label] ? fmtDur(tmprByDay[d.label]) : '—',
    tma: tmaByDay[d.label] ? fmtDur(tmaByDay[d.label]) : '—',
    pct: `${d.pctTotal}%`,
    isPeak: d.isPeak,
    isFds: d.isFds,
  }));
  return {
    rows,
    totals: { conv: rows.reduce((a, r) => a + r.conv, 0) },
  };
});

// ── SLA gauge ─────────────────────────────────────────────────────────────────
const resolutionRate = computed(() => {
  if (!summary.value) return 0;
  const conv = summary.value.conversations_count || 0;
  const resolved = summary.value.resolutions_count || 0;
  if (!conv) return 0;
  return Math.min(100, Math.round((resolved / conv) * 100));
});

const hasSlaPolicy = computed(
  () => (store.getters['sla/getSLA'] || []).length > 0
);

const firstResponseSla = computed(() =>
  slaScore(summary.value?.avg_first_response_time, 60)
);

const slaGaugeDash = computed(() => {
  const arc = firstResponseSla.value;
  return `${arc} ${100 - arc}`;
});

const slaArcStr = computed(() => `${firstResponseSla.value}%`);

const slaItems = computed(() => [
  {
    label: t('CHAT_DASHBOARD.SLA_1'),
    val: slaScore(summary.value?.avg_first_response_time, 60),
    color: '#00D4E0',
  },
  {
    label: t('CHAT_DASHBOARD.SLA_2'),
    val: slaScore(summary.value?.avg_resolution_time, 1800),
    color: '#F5A524',
  },
  {
    label: t('CHAT_DASHBOARD.SLA_3'),
    val: slaScore(summary.value?.reply_time, 120),
    color: '#2163FF',
  },
]);

// ── Gráfico de linha (TMPR vs TMA por hora do dia) ───────────────────────────
const lineChart = computed(() => {
  const W = 320;
  const H = 170;

  const tmprByHour = aggregateMeanByHour(hourlyTmprRaw.value).map(v =>
    v !== null ? v / 60 : null
  );
  const tmaByHour = aggregateMeanByHour(hourlyTmaRaw.value).map(v =>
    v !== null ? v / 60 : null
  );

  const activeHours = HEAT_HOURS;
  const tmprPts = activeHours.map(h => tmprByHour[h] || 0);
  const tmaPts = activeHours.map(h => tmaByHour[h] || 0);
  const allVals = [...tmprPts, ...tmaPts].filter(v => v > 0);
  const maxV = allVals.length ? Math.max(...allVals) : 1;
  const n = activeHours.length;

  const x = i => (i / (n - 1)) * W;
  const y = v => H - (v / maxV) * H * 0.85;
  const toPath = pts =>
    pts
      .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
      .join(' ');

  const toArea = pts => {
    const linePart = pts
      .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
      .join(' ');
    return `${linePart} L${W} ${H} L0 ${H} Z`;
  };

  return {
    tmpr: toPath(tmprPts),
    tma: toPath(tmaPts),
    tmprArea: toArea(tmprPts),
    tmaArea: toArea(tmaPts),
    dots: tmprPts.map((v, i) => ({ cx: x(i).toFixed(1), cy: y(v).toFixed(1) })),
    grid: [1, 2, 3].map(g => (H / 4) * g),
  };
});

// ── Agentes ───────────────────────────────────────────────────────────────────
const agentList = computed(() => {
  const agentMap = {};
  (storeAgentsData.value || []).forEach(a => {
    agentMap[a.id] = a;
  });
  return agentRaw.value
    .filter(a => a.conversations_count > 0)
    .sort((a, b) => b.conversations_count - a.conversations_count)
    .slice(0, 10)
    .map((a, i) => {
      const info = agentMap[a.id];
      const nm = info?.name || `Agente ${a.id}`;
      const role = info?.role || '—';
      const tmpr = fmtDur(a.avg_first_response_time);
      const tma = fmtDur(a.avg_resolution_time);
      const tmprSec = Number(a.avg_first_response_time) || 0;
      let sla;
      if (!tmprSec) {
        sla = 'warn';
      } else if (tmprSec < 60) {
        sla = 'ok';
      } else if (tmprSec < 300) {
        sla = 'warn';
      } else {
        sla = 'bad';
      }
      return {
        id: a.id,
        nm,
        role,
        conv: a.conversations_count,
        tma,
        tmpr,
        sla,
        color: AVATAR_COLORS[i % AVATAR_COLORS.length],
      };
    });
});

// ── CSAT / Satisfação ─────────────────────────────────────────────────────────
const csatPct = computed(() => {
  const d = csatRaw.value;
  if (!d || !d.total_count) return 0;
  const ratings = d.ratings_count || {};
  const pos = (Number(ratings['4']) || 0) + (Number(ratings['5']) || 0);
  return Math.round((pos / d.total_count) * 100);
});

const csatResponseRate = computed(() => {
  const d = csatRaw.value;
  const conv = summary.value?.conversations_count || 0;
  if (!d || !conv) return 0;
  return Math.min(100, Math.round((d.total_count / conv) * 100));
});

const satisfaction = computed(() => [
  {
    nm: 'CSAT',
    val: csatPct.value ? `${csatPct.value}%` : '—',
    pct: csatPct.value,
    desc: t('CHAT_DASHBOARD.CSAT_DESC'),
    color: '#19C37D',
  },
  {
    nm: t('CHAT_DASHBOARD.CSAT_RESP_RATE'),
    val: csatResponseRate.value ? `${csatResponseRate.value}%` : '—',
    pct: csatResponseRate.value,
    desc: t('CHAT_DASHBOARD.CSAT_RESP_DESC'),
    color: '#00D4E0',
  },
  {
    nm: t('CHAT_DASHBOARD.RES_RATE'),
    val: resolutionRate.value ? `${resolutionRate.value}%` : '—',
    pct: resolutionRate.value,
    desc: t('CHAT_DASHBOARD.RES_RATE_DESC'),
    color: '#F5A524',
  },
  {
    nm: t('CHAT_DASHBOARD.BOT_RATE'),
    val: botKpi.value.val,
    pct: Number(botKpi.value.val.replace('%', '')) || 0,
    desc: t('CHAT_DASHBOARD.BOT_RATE_DESC'),
    color: '#2163FF',
  },
]);

// ── Tópicos (etiquetas) ───────────────────────────────────────────────────────
const topics = computed(() =>
  [...labelRaw.value]
    .filter(l => (l.conversations_count || 0) > 0)
    .sort((a, b) => b.conversations_count - a.conversations_count)
    .slice(0, 6)
    .map(l => ({
      nm: l.name || l.title || '—',
      ct: l.conversations_count || 0,
    }))
);

const topicMax = computed(() =>
  topics.value.length ? Math.max(...topics.value.map(tp => tp.ct)) : 1
);

// ── Export ───────────────────────────────────────────────────────────────────
const exportCSV = () => {
  const BOM = '﻿';
  let csv = BOM + 'Data;Dia;Conversas;TMPR;TMA;% Total\n';
  dailyAna.value.rows.forEach(r => {
    csv += `${r.date};${r.dow};${r.conv};${r.tmpr};${r.tma};${r.pct}\n`;
  });
  if (hourlyAna.value.rows.length) {
    csv += '\nFaixa;Conversas;TMPR;% Total\n';
    hourlyAna.value.rows.forEach(r => {
      csv += `${r.label};${r.conv};${r.tmpr};${r.pct}\n`;
    });
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `talkchat-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportPDF = () => window.print();

// ── Estilos constantes (evitar vue/no-static-inline-styles) ──────────────────
const HEAT_GRID_COLS = { gridTemplateColumns: '42px repeat(13, 1fr)' };
const HEAT_LEGEND_BG = {
  background: 'linear-gradient(90deg, rgba(0,212,224,.15), #00D4E0, #2163FF)',
};
const LEGEND_TMPR_STYLE = { background: '#2163FF' };
const LEGEND_TMA_STYLE = { background: '#00D4E0' };
</script>

<template>
  <div class="flex flex-col gap-6 p-6 max-w-screen-xl mx-auto pb-16">
    <!-- Cabeçalho da página -->
    <div class="flex items-start gap-4">
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <span class="text-xs font-bold tracking-widest uppercase text-n-teal-9">
          {{ t('CHAT_DASHBOARD.EYEBROW') }}
        </span>
        <h1 class="text-3xl font-black tracking-tight text-n-slate-12">
          {{ t('CHAT_DASHBOARD.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 font-medium">
          {{ t('CHAT_DASHBOARD.SUBTITLE') }}
        </p>
      </div>

      <!-- Controles: período + exportar -->
      <div class="flex flex-col items-end gap-2 flex-shrink-0">
        <span
          class="text-xs font-bold tracking-wider uppercase text-n-slate-11"
        >
          {{ t('CHAT_DASHBOARD.PERIOD') }}
        </span>
        <div class="flex items-center gap-2">
          <!-- Reload -->
          <button
            class="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-n-slate-11 hover:text-n-slate-12 border border-n-weak rounded-xl bg-n-surface-1 hover:bg-n-surface-2 transition-all"
            :class="{ 'opacity-50 cursor-not-allowed': loading }"
            :disabled="loading"
            @click="fetchData"
          >
            {{ t('CHAT_DASHBOARD.RELOAD') }}
          </button>
          <!-- Exportar -->
          <div class="relative">
            <div
              v-if="showExport"
              class="fixed inset-0 z-10"
              @click="showExport = false"
            />
            <button
              class="relative z-20 flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-n-slate-11 hover:text-n-slate-12 border border-n-weak rounded-xl bg-n-surface-1 hover:bg-n-surface-2 transition-all"
              @click="showExport = !showExport"
            >
              {{ t('CHAT_DASHBOARD.EXPORT') }}
            </button>
            <div
              v-if="showExport"
              class="absolute right-0 top-full mt-1 bg-n-surface-2 border border-n-weak rounded-xl shadow-xl z-20 min-w-[160px] p-1 flex flex-col"
            >
              <button
                class="text-left text-xs font-bold text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 px-3 py-2 rounded-lg transition-colors"
                @click="
                  exportCSV();
                  showExport = false;
                "
              >
                {{ t('CHAT_DASHBOARD.EXPORT_CSV') }}
              </button>
              <button
                class="text-left text-xs font-bold text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 px-3 py-2 rounded-lg transition-colors"
                @click="
                  exportPDF();
                  showExport = false;
                "
              >
                {{ t('CHAT_DASHBOARD.EXPORT_PDF') }}
              </button>
            </div>
          </div>
          <!-- Período -->
          <div
            class="flex gap-1 p-1 rounded-xl border border-n-weak bg-n-surface-1"
          >
            <button
              v-for="p in ['hoje', 'semana', 'mes']"
              :key="p"
              class="px-4 py-2 text-sm font-bold rounded-lg transition-all"
              :class="
                period === p
                  ? 'text-white bg-gradient-to-r from-n-teal-9 to-n-blue-9 shadow'
                  : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="period = p"
            >
              {{ t(`CHAT_DASHBOARD.PERIOD_${p.toUpperCase()}`) }}
            </button>
            <button
              class="px-4 py-2 text-sm font-bold rounded-lg transition-all"
              :class="
                period === 'personalizado'
                  ? 'text-white bg-gradient-to-r from-n-teal-9 to-n-blue-9 shadow'
                  : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="period = 'personalizado'"
            >
              {{
                period === 'personalizado' && customLabel
                  ? customLabel
                  : t('CHAT_DASHBOARD.PERIOD_PERSONALIZADO')
              }}
            </button>
          </div>
        </div>
        <!-- Date picker para período personalizado -->
        <div v-if="period === 'personalizado'">
          <WootDatePicker
            :date-range="customDateRange || []"
            align="right"
            @date-range-changed="onCustomRangeChanged"
          />
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-surface-1/70 backdrop-blur-sm"
    >
      <div
        class="w-10 h-10 rounded-full border-4 border-n-teal-9 border-t-transparent animate-spin"
      />
    </div>

    <!-- ── SEÇÃO 01: OPERACIONAL & VOLUME ─────────────────────────────────── -->
    <div class="flex items-center gap-3">
      <span
        class="text-xs font-mono font-black text-n-teal-9 bg-n-teal-9/10 border border-n-teal-9/25 px-2 py-1 rounded-lg"
        >{{ t('CHAT_DASHBOARD.SEC_1') }}</span
      >
      <h2 class="text-lg font-black text-n-slate-12">
        {{ t('CHAT_DASHBOARD.S1_TITLE') }}
      </h2>
      <div
        class="flex-1 h-px bg-gradient-to-r from-n-teal-9 via-n-blue-9 to-transparent"
      />
      <span class="text-xs text-n-slate-11 font-semibold">{{
        t('CHAT_DASHBOARD.S1_SUB')
      }}</span>
    </div>

    <!-- KPIs -->
    <div class="grid grid-cols-2 xl:grid-cols-4 gap-4">
      <div
        v-for="(kpi, ki) in [
          {
            label: t('CHAT_DASHBOARD.KPI_CONV'),
            val: convKpi.val,
            delta: convKpi.delta,
            dir: convKpi.dir,
            vs: vsLabel,
            accent: '#00D4E0',
          },
          {
            label: t('CHAT_DASHBOARD.KPI_TMPR'),
            val: tmprKpi.val,
            delta: tmprKpi.delta,
            dir: tmprKpi.dir,
            vs: t('CHAT_DASHBOARD.FASTER'),
            accent: '#2163FF',
          },
          {
            label: t('CHAT_DASHBOARD.KPI_PICO'),
            val: peakHour,
            delta: '',
            dir: '',
            vs: t('CHAT_DASHBOARD.KPI_PICO_VS'),
            accent: '#F5A524',
          },
          {
            label: t('CHAT_DASHBOARD.KPI_BOT'),
            val: botKpi.val,
            delta: botKpi.delta,
            dir: botKpi.dir,
            vs: t('CHAT_DASHBOARD.KPI_BOT_VS'),
            accent: '#19C37D',
          },
        ]"
        :key="ki"
        class="relative card bg-n-surface-1 border border-n-weak rounded-2xl p-5 pl-6 shadow-sm hover:border-n-teal-9/30 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out"
      >
        <div
          class="absolute inset-0 rounded-2xl pointer-events-none"
          :style="{ background: kpi.accent + '0a' }"
        />
        <div
          class="absolute left-0 top-4 bottom-4 w-1 rounded-r"
          :style="{ background: kpi.accent }"
        />
        <div class="text-xs text-n-slate-11 font-semibold">{{ kpi.label }}</div>
        <div
          class="text-3xl font-black tracking-tight text-n-slate-12 my-2 tabular-nums"
        >
          {{ kpi.val }}
        </div>
        <div
          class="text-xs font-bold inline-flex items-center gap-1"
          :class="
            kpi.dir === 'up'
              ? 'text-n-teal-10'
              : kpi.dir === 'down'
                ? 'text-n-ruby-9'
                : 'text-n-slate-11'
          "
        >
          {{ kpi.delta }}
          <span class="font-normal text-n-slate-11">{{ kpi.vs }}</span>
        </div>
      </div>
    </div>

    <!-- Volume + Canal -->
    <div class="grid grid-cols-1 xl:grid-cols-5 gap-4">
      <!-- Volume barras -->
      <div
        class="xl:col-span-3 bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <h3 class="font-black text-n-slate-12 mb-1">
          {{ t('CHAT_DASHBOARD.VOL_TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mb-4">
          {{ t('CHAT_DASHBOARD.VOL_CAP') }}
        </p>
        <div class="flex items-end gap-2 h-44">
          <div
            v-for="bar in volumeBars"
            :key="bar.label"
            class="flex-1 flex flex-col items-center gap-1.5 h-full justify-end relative group"
          >
            <span
              class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-n-slate-12 text-n-slate-1 text-[10px] font-bold px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10"
            >
              {{ bar.label }} {{ t('CHAT_DASHBOARD.H_SEP') }}
              {{ fmt(bar.atd) }}</span
            >
            <span
              v-if="bar.atd > 0"
              class="text-[9px] font-bold text-n-slate-11 tabular-nums leading-none"
            >{{ fmt(bar.atd) }}</span>
            <div class="w-full flex flex-col items-center max-w-[34px]">
              <div
                class="w-full rounded-t transition-all duration-500 bg-n-teal-9/25"
                :style="{ height: bar.hInativPct * 0.9 + 'px' }"
              />
              <div
                class="w-full rounded-t transition-all duration-500 bg-gradient-to-t from-n-blue-9 to-n-teal-9 group-hover:brightness-110"
                :style="{ height: bar.hAtdPct * 0.9 + 'px' }"
              />
            </div>
            <span
              class="text-[9px] font-bold"
              :class="bar.showLabel ? 'text-n-slate-11' : 'text-transparent'"
              >{{ bar.showLabel ? bar.label : '·' }}</span
            >
          </div>
        </div>
      </div>

      <!-- Canal donut -->
      <div
        class="xl:col-span-2 bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <h3 class="font-black text-n-slate-12 mb-1">
          {{ t('CHAT_DASHBOARD.CHANNEL_TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mb-4">
          {{ t('CHAT_DASHBOARD.CHANNEL_CAP') }}
        </p>
        <div class="flex items-center gap-4">
          <svg
            width="132"
            height="132"
            viewBox="0 0 42 42"
            class="flex-shrink-0"
          >
            <circle
              cx="21"
              cy="21"
              r="15.9"
              fill="none"
              stroke="currentColor"
              stroke-width="7"
              class="text-n-slate-4/30"
            />
            <circle
              v-for="seg in donutSegments"
              :key="seg.label"
              cx="21"
              cy="21"
              r="15.9"
              fill="none"
              :stroke="seg.color"
              stroke-width="7"
              :stroke-dasharray="`${seg.pc} ${100 - seg.pc}`"
              :stroke-dashoffset="seg.offset"
            />
          </svg>
          <div class="flex flex-col gap-2.5 flex-1">
            <div
              v-for="seg in donutSegments"
              :key="seg.label"
              class="flex items-center gap-2 text-sm"
            >
              <span
                class="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                :style="{ background: seg.color }"
              />
              <span class="flex-1 text-n-slate-11 font-semibold text-xs">{{
                seg.label
              }}</span>
              <span class="font-black text-n-slate-12 text-xs tabular-nums"
                >{{ fmt(seg.total) }}
                <span class="text-n-slate-11 font-normal"
                  >({{ seg.pc }}%)</span
                ></span
              >
            </div>
            <p
              v-if="!donutSegments.length"
              class="text-xs text-n-slate-11 italic"
            >
              {{ t('CHAT_DASHBOARD.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Heatmap -->
    <div
      class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <h3 class="font-black text-n-slate-12 mb-1">
        {{ t('CHAT_DASHBOARD.HEAT_TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ t('CHAT_DASHBOARD.HEAT_CAP') }}
      </p>
      <div class="overflow-x-auto">
        <div class="min-w-[560px]">
          <!-- Cabeçalho horas -->
          <div :style="HEAT_GRID_COLS" class="grid gap-1 mb-1 pl-1">
            <div />
            <div
              v-for="h in HEAT_HOURS"
              :key="h"
              class="text-center text-[9px] font-bold text-n-slate-11"
            >
              {{ `${h}h` }}
            </div>
          </div>
          <!-- Linhas dias -->
          <div
            v-for="(row, di) in heatmap"
            :key="di"
            :style="HEAT_GRID_COLS"
            class="grid gap-1 mb-1"
          >
            <div class="text-[9px] font-bold text-n-slate-11 flex items-center">
              {{ HEAT_DAYS[di] }}
            </div>
            <div
              v-for="cell in row"
              :key="cell.h"
              class="h-7 rounded-md transition-opacity duration-200 hover:ring-1 hover:ring-n-teal-9/40 cursor-default flex items-center justify-center"
              :style="{ background: `rgba(0,212,224,${cell.opacity})` }"
            >
              <span
                v-if="cell.val > 0"
                class="text-[8px] font-bold leading-none select-none"
                :style="{ color: cell.opacity > 0.5 ? '#fff' : 'rgba(0,0,0,0.55)' }"
              >{{ cell.val }}</span>
            </div>
          </div>
          <!-- Legenda -->
          <div class="flex items-center gap-2 mt-3 justify-end">
            <span class="text-[9px] text-n-slate-11 font-bold">{{
              t('CHAT_DASHBOARD.HEAT_LESS')
            }}</span>
            <div class="w-24 h-2 rounded" :style="HEAT_LEGEND_BG" />
            <span class="text-[9px] text-n-slate-11 font-bold">{{
              t('CHAT_DASHBOARD.HEAT_MORE')
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Volumetria hora a hora -->
    <div
      class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 class="font-black text-n-slate-12">
            {{ t('CHAT_DASHBOARD.HOURLY_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('CHAT_DASHBOARD.HOURLY_CAP') }}
          </p>
        </div>
        <div
          class="flex gap-1 p-1 rounded-lg border border-n-weak bg-n-surface-2 flex-shrink-0"
        >
          <button
            v-for="m in ['sint', 'ana']"
            :key="m"
            class="px-3 py-1.5 text-xs font-bold rounded-md transition-all"
            :class="
              hourlyView === m
                ? 'text-white bg-gradient-to-r from-n-teal-9 to-n-blue-9'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="hourlyView = m"
          >
            {{
              m === 'sint'
                ? t('CHAT_DASHBOARD.SYNTHETIC')
                : t('CHAT_DASHBOARD.ANALYTICAL')
            }}
          </button>
        </div>
      </div>

      <!-- Sintético -->
      <div v-if="hourlyView === 'sint'">
        <div class="flex items-end gap-0.5 h-44">
          <div
            v-for="bar in hourlyBars"
            :key="bar.i"
            class="flex-1 flex flex-col items-center gap-1 h-full justify-end relative group"
          >
            <span
              v-if="bar.v > 0"
              class="text-[9px] font-bold text-n-slate-11 tabular-nums leading-none"
            >{{ fmt(bar.v) }}</span>
            <div
              class="w-full transition-all duration-500 rounded-t min-h-0.5"
              :class="
                bar.isPeak
                  ? 'bg-n-blue-9'
                  : bar.isLow
                    ? 'bg-n-teal-9/25'
                    : 'bg-n-teal-9'
              "
              :style="{ height: bar.pct + '%' }"
            />
            <span class="text-[9px] font-bold text-n-slate-11">{{ bar.label }}</span>
          </div>
        </div>
        <div
          class="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-n-weak"
        >
          <div
            v-for="(val, key) in hourlyStats"
            :key="key"
            class="flex flex-col gap-1"
          >
            <span
              class="text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{
                key === 'commercial'
                  ? t('CHAT_DASHBOARD.STAT_COMMERCIAL', {
                      start: pad2(commercialWindow.start),
                      end: pad2(commercialWindow.end),
                    })
                  : t(`CHAT_DASHBOARD.STAT_${key.toUpperCase()}`)
              }}
            </span>
            <span class="text-sm font-black text-n-slate-12">{{ val }}</span>
          </div>
        </div>
      </div>

      <!-- Analítico horário -->
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm min-w-[480px]">
          <thead>
            <tr class="border-b border-n-weak">
              <th
                class="pb-2 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_FAIXA') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_CONVERSAS') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_TMPR') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_PCT_TOTAL') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in hourlyAna.rows"
              :key="row.label"
              class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
              :class="row.isPeak ? 'bg-n-blue-9/10' : ''"
            >
              <td
                class="py-2.5 text-n-slate-12 font-semibold tabular-nums"
                :class="row.isPeak ? 'text-n-teal-9' : ''"
              >
                {{ row.label }}
              </td>
              <td
                class="py-2.5 text-right font-black text-n-slate-12 tabular-nums"
              >
                {{ fmt(row.conv) }}
              </td>
              <td class="py-2.5 text-right text-n-slate-11 tabular-nums">
                {{ row.tmpr }}
              </td>
              <td class="py-2.5 text-right text-n-slate-11 tabular-nums">
                {{ row.pct }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-n-weak font-black">
              <td class="pt-2.5">{{ t('CHAT_DASHBOARD.TOTAL') }}</td>
              <td class="pt-2.5 text-right tabular-nums">
                {{ fmt(hourlyAna.totals.conv) }}
              </td>
              <td class="pt-2.5 text-right">{{ t('CHAT_DASHBOARD.DASH') }}</td>
              <td class="pt-2.5 text-right">
                {{ t('CHAT_DASHBOARD.TOTAL_PCT') }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Volumetria por data -->
    <div
      class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 class="font-black text-n-slate-12">
            {{ t('CHAT_DASHBOARD.DAILY_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('CHAT_DASHBOARD.DAILY_CAP') }}
          </p>
        </div>
        <div
          class="flex gap-1 p-1 rounded-lg border border-n-weak bg-n-surface-2 flex-shrink-0"
        >
          <button
            v-for="m in ['sint', 'ana']"
            :key="m"
            class="px-3 py-1.5 text-xs font-bold rounded-md transition-all"
            :class="
              dailyView === m
                ? 'text-white bg-gradient-to-r from-n-teal-9 to-n-blue-9'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="dailyView = m"
          >
            {{
              m === 'sint'
                ? t('CHAT_DASHBOARD.SYNTHETIC')
                : t('CHAT_DASHBOARD.ANALYTICAL')
            }}
          </button>
        </div>
      </div>

      <!-- Sintético -->
      <div v-if="dailyView === 'sint'">
        <div class="flex items-end gap-0.5 h-44">
          <div
            v-for="bar in dailySeries"
            :key="bar.label"
            class="flex-1 flex flex-col items-center gap-1 h-full justify-end relative group"
          >
            <span
              class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-n-slate-12 text-n-slate-1 text-[10px] font-bold px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10"
              >{{ bar.label }} {{ t('CHAT_DASHBOARD.H_SEP') }}
              {{ fmt(bar.v) }}</span
            >
            <span
              v-if="bar.v > 0"
              class="text-[9px] font-bold text-n-slate-11 tabular-nums leading-none"
            >{{ fmt(bar.v) }}</span>
            <div
              class="w-full transition-all duration-500 rounded-t min-h-0.5"
              :class="
                bar.isPeak
                  ? 'bg-n-blue-9'
                  : bar.isLow
                    ? 'bg-n-teal-9/25'
                    : 'bg-n-teal-9'
              "
              :style="{ height: bar.pct + '%' }"
            />
            <span
              class="text-[9px] font-bold"
              :class="bar.showLabel ? 'text-n-slate-11' : 'text-transparent'"
            >
              {{ bar.showLabel ? bar.label : '·' }}
            </span>
          </div>
        </div>
        <div
          class="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-n-weak"
        >
          <div
            v-for="(val, key) in dailyStats"
            :key="key"
            class="flex flex-col gap-1"
          >
            <span
              class="text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t(`CHAT_DASHBOARD.DSTAT_${key.toUpperCase()}`) }}
            </span>
            <span class="text-sm font-black text-n-slate-12">{{ val }}</span>
          </div>
        </div>
      </div>

      <!-- Analítico diário -->
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm min-w-[600px]">
          <thead>
            <tr class="border-b border-n-weak">
              <th
                class="pb-2 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_DATA') }}
              </th>
              <th
                class="pb-2 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_DIA') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_CONVERSAS') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_TMPR') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_TMA_COL') }}
              </th>
              <th
                class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
              >
                {{ t('CHAT_DASHBOARD.TH_PCT_TOTAL') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in dailyAna.rows"
              :key="row.date"
              class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
              :class="{
                'bg-n-blue-9/10': row.isPeak,
                'bg-n-slate-4/30': row.isFds && !row.isPeak,
              }"
            >
              <td
                class="py-2 tabular-nums font-semibold"
                :class="row.isPeak ? 'text-n-teal-9' : 'text-n-slate-12'"
              >
                {{ row.date }}
              </td>
              <td
                class="py-2 text-xs font-bold"
                :class="row.isFds ? 'text-n-teal-9' : 'text-n-slate-11'"
              >
                {{ row.dow }}
              </td>
              <td
                class="py-2 text-right font-black text-n-slate-12 tabular-nums"
              >
                {{ fmt(row.conv) }}
              </td>
              <td class="py-2 text-right text-n-slate-11 tabular-nums">
                {{ row.tmpr }}
              </td>
              <td class="py-2 text-right text-n-slate-11 tabular-nums">
                {{ row.tma }}
              </td>
              <td class="py-2 text-right text-n-slate-11 tabular-nums">
                {{ row.pct }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-n-weak font-black">
              <td class="pt-2.5" colspan="2">
                {{ t('CHAT_DASHBOARD.TOTAL') }}
              </td>
              <td class="pt-2.5 text-right tabular-nums">
                {{ fmt(dailyAna.totals.conv) }}
              </td>
              <td class="pt-2.5 text-right">{{ t('CHAT_DASHBOARD.DASH') }}</td>
              <td class="pt-2.5 text-right">{{ t('CHAT_DASHBOARD.DASH') }}</td>
              <td class="pt-2.5 text-right">
                {{ t('CHAT_DASHBOARD.TOTAL_PCT') }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- ── SEÇÃO 02: DESEMPENHO & SLA ──────────────────────────────────────── -->
    <div class="flex items-center gap-3">
      <span
        class="text-xs font-mono font-black text-n-teal-9 bg-n-teal-9/10 border border-n-teal-9/25 px-2 py-1 rounded-lg"
        >{{ t('CHAT_DASHBOARD.SEC_2') }}</span
      >
      <h2 class="text-lg font-black text-n-slate-12">
        {{ t('CHAT_DASHBOARD.S2_TITLE') }}
      </h2>
      <div
        class="flex-1 h-px bg-gradient-to-r from-n-teal-9 via-n-blue-9 to-transparent"
      />
      <span class="text-xs text-n-slate-11 font-semibold">{{
        t('CHAT_DASHBOARD.S2_SUB')
      }}</span>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <!-- Gauge de resolução -->
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <h3 class="font-black text-n-slate-12 mb-1">
          {{ t('CHAT_DASHBOARD.SLA_TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mb-4">
          {{ t('CHAT_DASHBOARD.SLA_CAP') }}
        </p>
        <div v-if="hasSlaPolicy" class="flex items-center gap-6">
          <!-- Gauge SVG -->
          <div class="relative w-36 h-36 flex-shrink-0">
            <svg width="144" height="144" viewBox="0 0 42 42">
              <circle
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke="currentColor"
                stroke-width="5"
                class="text-n-slate-4/30"
              />
              <circle
                cx="21"
                cy="21"
                r="15.9"
                fill="none"
                stroke="#00D4E0"
                stroke-width="5"
                :stroke-dasharray="slaGaugeDash"
                stroke-linecap="round"
                transform="rotate(-90 21 21)"
              />
            </svg>
            <div
              class="absolute inset-0 flex flex-col items-center justify-center"
            >
              <span class="text-2xl font-black text-n-slate-12 tabular-nums">{{
                slaArcStr
              }}</span>
              <span
                class="text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
                >{{ t('CHAT_DASHBOARD.SLA_ON_TIME') }}</span
              >
            </div>
          </div>
          <!-- Barras SLA -->
          <div class="flex-1 flex flex-col gap-3">
            <div
              v-for="(sla, si) in slaItems"
              :key="si"
              class="flex flex-col gap-1.5"
            >
              <div
                class="flex justify-between text-xs text-n-slate-11 font-medium"
              >
                <span>{{ sla.label }}</span>
                <span class="font-black text-n-slate-12">{{ sla.val }}%</span>
              </div>
              <div class="h-1.5 bg-n-slate-4/30 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-700"
                  :style="{ width: sla.val + '%', background: sla.color }"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center gap-2 py-8 text-center"
        >
          <span class="i-lucide-chart-no-axes-column text-3xl text-n-slate-7" />
          <p class="text-sm text-n-slate-10">
            {{ t('CHAT_DASHBOARD.SLA_NO_DATA') }}
          </p>
        </div>
      </div>

      <!-- Gráfico de linha -->
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <h3 class="font-black text-n-slate-12 mb-1">
          {{ t('CHAT_DASHBOARD.LINE_TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11 mb-4">
          {{ t('CHAT_DASHBOARD.LINE_CAP') }}
        </p>
        <svg class="w-full" viewBox="0 0 320 170" preserveAspectRatio="none">
          <defs>
            <linearGradient id="grad-tmpr" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#2163FF" stop-opacity="0.18" />
              <stop offset="100%" stop-color="#2163FF" stop-opacity="0" />
            </linearGradient>
            <linearGradient id="grad-tma" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#00D4E0" stop-opacity="0.14" />
              <stop offset="100%" stop-color="#00D4E0" stop-opacity="0" />
            </linearGradient>
          </defs>
          <line
            v-for="y in lineChart.grid"
            :key="y"
            x1="0"
            :y1="y"
            x2="320"
            :y2="y"
            stroke="currentColor"
            stroke-width="1"
            class="text-n-slate-4/40"
          />
          <path :d="lineChart.tmaArea" fill="url(#grad-tma)" stroke="none" />
          <path :d="lineChart.tmprArea" fill="url(#grad-tmpr)" stroke="none" />
          <path
            :d="lineChart.tma"
            fill="none"
            stroke="#00D4E0"
            stroke-width="2.4"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <path
            :d="lineChart.tmpr"
            fill="none"
            stroke="#2163FF"
            stroke-width="2.4"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
          <circle
            v-for="(dot, di) in lineChart.dots"
            :key="di"
            :cx="dot.cx"
            :cy="dot.cy"
            r="3"
            fill="#fff"
            stroke="#2163FF"
            stroke-width="1.8"
          />
        </svg>
        <div class="flex gap-4 mt-3">
          <div
            class="flex items-center gap-2 text-xs text-n-slate-11 font-semibold"
          >
            <span class="w-4 h-0.5 rounded" :style="LEGEND_TMPR_STYLE" />
            {{ t('CHAT_DASHBOARD.LEGEND_TMPR') }}
          </div>
          <div
            class="flex items-center gap-2 text-xs text-n-slate-11 font-semibold"
          >
            <span class="w-4 h-0.5 rounded" :style="LEGEND_TMA_STYLE" />
            {{ t('CHAT_DASHBOARD.LEGEND_TMA') }}
          </div>
        </div>
      </div>
    </div>

    <!-- KPIs SLA -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div
        v-for="(kpi, ki) in [
          {
            label: t('CHAT_DASHBOARD.KPI_TMPR'),
            val: tmprKpi.val,
            delta: tmprKpi.delta,
            dir: tmprKpi.dir,
            vs: t('CHAT_DASHBOARD.FASTER'),
            accent: '#2163FF',
          },
          {
            label: t('CHAT_DASHBOARD.KPI_TMA'),
            val: tmaKpi.val,
            delta: tmaKpi.delta,
            dir: tmaKpi.dir,
            vs: t('CHAT_DASHBOARD.ATTENTION'),
            accent: '#00D4E0',
          },
          {
            label: t('CHAT_DASHBOARD.KPI_QUEUE'),
            val: queueKpi.val,
            delta: queueKpi.delta,
            dir: queueKpi.dir,
            vs: t('CHAT_DASHBOARD.BETTER'),
            accent: '#F5A524',
          },
        ]"
        :key="ki"
        class="relative bg-n-surface-1 border border-n-weak rounded-2xl p-5 pl-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ease-out"
      >
        <div
          class="absolute inset-0 rounded-2xl pointer-events-none"
          :style="{ background: kpi.accent + '0a' }"
        />
        <div
          class="absolute left-0 top-4 bottom-4 w-1 rounded-r"
          :style="{ background: kpi.accent }"
        />
        <div class="text-xs text-n-slate-11 font-semibold">{{ kpi.label }}</div>
        <div
          class="text-3xl font-black tracking-tight text-n-slate-12 my-2 tabular-nums"
        >
          {{ kpi.val }}
        </div>
        <div
          class="text-xs font-bold inline-flex items-center gap-1"
          :class="kpi.dir === 'up' ? 'text-n-teal-10' : 'text-n-ruby-9'"
        >
          {{ kpi.delta }}
          <span class="font-normal text-n-slate-11">{{ kpi.vs }}</span>
        </div>
      </div>
    </div>

    <!-- SLA em risco (tempo real) -->
    <div
      v-if="hasSlaPolicy"
      class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <h3 class="font-black text-n-slate-12 mb-1">
        {{ t('CHAT_DASHBOARD.SLA_RISK_TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ t('CHAT_DASHBOARD.SLA_RISK_CAP') }}
      </p>
      <template v-if="slaRisk.length">
        <div class="overflow-x-auto">
          <table class="w-full text-sm min-w-[500px]">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="pb-2 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
                >
                  {{ t('CHAT_DASHBOARD.SLA_RISK_TH_ID') }}
                </th>
                <th
                  class="pb-2 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
                >
                  {{ t('CHAT_DASHBOARD.SLA_RISK_TH_AGENT') }}
                </th>
                <th
                  class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
                >
                  {{ t('CHAT_DASHBOARD.SLA_RISK_TH_WAIT') }}
                </th>
                <th
                  class="pb-2 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
                >
                  {{ t('CHAT_DASHBOARD.SLA_RISK_TH_STATUS') }}
                </th>
                <th class="pb-2" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in slaRisk"
                :key="c.id"
                class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
                :class="
                  c.status === 'breached' ? 'bg-n-ruby-9/5' : 'bg-n-amber-9/5'
                "
              >
                <td
                  class="py-2.5 font-mono font-bold text-n-slate-12 tabular-nums"
                >
                  #{{ c.id }}
                </td>
                <td class="py-2.5 text-n-slate-11">{{ c.agent }}</td>
                <td
                  class="py-2.5 text-right font-bold tabular-nums"
                  :class="
                    c.status === 'breached'
                      ? 'text-n-ruby-9'
                      : 'text-n-amber-10'
                  "
                >
                  {{ fmtWait(c.waited) }}
                </td>
                <td class="py-2.5 text-right">
                  <span
                    class="text-xs font-black px-2 py-0.5 rounded-full"
                    :class="
                      c.status === 'breached'
                        ? 'bg-n-ruby-9/15 text-n-ruby-9'
                        : 'bg-n-amber-9/15 text-n-amber-10'
                    "
                  >
                    {{
                      c.status === 'breached'
                        ? t('CHAT_DASHBOARD.SLA_RISK_BREACHED')
                        : t('CHAT_DASHBOARD.SLA_RISK_AT_RISK')
                    }}
                  </span>
                </td>
                <td class="py-2.5 text-right">
                  <a
                    :href="c.link"
                    class="text-xs font-bold text-n-teal-9 hover:text-n-teal-10 hover:underline transition-colors"
                  >
                    {{ t('CHAT_DASHBOARD.SLA_RISK_OPEN') }}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p
        v-else
        class="text-sm font-bold text-n-teal-10 flex items-center gap-2"
      >
        {{ t('CHAT_DASHBOARD.SLA_RISK_CLEAR') }}
      </p>
    </div>

    <!-- ── SEÇÃO 03: AGENTES ───────────────────────────────────────────────── -->
    <div class="flex items-center gap-3">
      <span
        class="text-xs font-mono font-black text-n-teal-9 bg-n-teal-9/10 border border-n-teal-9/25 px-2 py-1 rounded-lg"
        >{{ t('CHAT_DASHBOARD.SEC_3') }}</span
      >
      <h2 class="text-lg font-black text-n-slate-12">
        {{ t('CHAT_DASHBOARD.S3_TITLE') }}
      </h2>
      <div
        class="flex-1 h-px bg-gradient-to-r from-n-teal-9 via-n-blue-9 to-transparent"
      />
      <span class="text-xs text-n-slate-11 font-semibold">{{
        t('CHAT_DASHBOARD.S3_SUB')
      }}</span>
    </div>

    <div
      class="bg-n-surface-1 border border-n-weak rounded-2xl overflow-hidden shadow-sm"
    >
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              class="px-5 py-3 text-left text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t('CHAT_DASHBOARD.TH_AGENT') }}
            </th>
            <th
              class="px-4 py-3 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t('CHAT_DASHBOARD.TH_CONV') }}
            </th>
            <th
              class="px-4 py-3 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t('CHAT_DASHBOARD.TH_TMPR') }}
            </th>
            <th
              class="px-4 py-3 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t('CHAT_DASHBOARD.TH_TMA') }}
            </th>
            <th
              class="px-4 py-3 text-right text-[10px] uppercase tracking-wider font-bold text-n-slate-11"
            >
              {{ t('CHAT_DASHBOARD.TH_SLA') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in agentList"
            :key="agent.id"
            class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-5 py-3">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                  :style="{ background: agent.color }"
                >
                  {{ initials(agent.nm) }}
                </div>
                <div>
                  <div class="font-bold text-n-slate-12">{{ agent.nm }}</div>
                  <div class="text-xs text-n-slate-11">{{ agent.role }}</div>
                </div>
              </div>
            </td>
            <td
              class="px-4 py-3 text-right font-black text-n-slate-12 tabular-nums"
            >
              {{ fmt(agent.conv) }}
            </td>
            <td class="px-4 py-3 text-right text-n-slate-11 tabular-nums">
              {{ agent.tmpr }}
            </td>
            <td class="px-4 py-3 text-right text-n-slate-11 tabular-nums">
              {{ agent.tma }}
            </td>
            <td class="px-4 py-3 text-right">
              <span
                class="text-xs font-black px-2.5 py-1 rounded-full"
                :class="{
                  'bg-n-teal-9/15 text-n-teal-10': agent.sla === 'ok',
                  'bg-n-amber-9/15 text-n-amber-10': agent.sla === 'warn',
                  'bg-n-ruby-9/15 text-n-ruby-9': agent.sla === 'bad',
                }"
              >
                {{
                  agent.sla === 'ok'
                    ? t('CHAT_DASHBOARD.SLA_OK')
                    : agent.sla === 'warn'
                      ? t('CHAT_DASHBOARD.SLA_WARN')
                      : t('CHAT_DASHBOARD.SLA_BAD')
                }}
              </span>
            </td>
          </tr>
          <tr v-if="!agentList.length">
            <td
              colspan="5"
              class="px-5 py-6 text-center text-xs text-n-slate-11 italic"
            >
              {{ t('CHAT_DASHBOARD.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── SEÇÃO 04: SATISFAÇÃO ───────────────────────────────────────────── -->
    <div class="flex items-center gap-3">
      <span
        class="text-xs font-mono font-black text-n-teal-9 bg-n-teal-9/10 border border-n-teal-9/25 px-2 py-1 rounded-lg"
        >{{ t('CHAT_DASHBOARD.SEC_4') }}</span
      >
      <h2 class="text-lg font-black text-n-slate-12">
        {{ t('CHAT_DASHBOARD.S4_TITLE') }}
      </h2>
      <div
        class="flex-1 h-px bg-gradient-to-r from-n-teal-9 via-n-blue-9 to-transparent"
      />
      <span class="text-xs text-n-slate-11 font-semibold">{{
        t('CHAT_DASHBOARD.S4_SUB')
      }}</span>
    </div>

    <!-- Cards de satisfação -->
    <div class="grid grid-cols-2 xl:grid-cols-4 gap-4">
      <div
        v-for="sat in satisfaction"
        :key="sat.nm"
        class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 text-center shadow-sm hover:border-n-teal-9/30 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 ease-out"
      >
        <!-- Ring SVG -->
        <div class="relative w-24 h-24 mx-auto mb-3">
          <svg width="96" height="96" viewBox="0 0 42 42">
            <circle
              cx="21"
              cy="21"
              r="15.9"
              fill="none"
              stroke="currentColor"
              stroke-width="4"
              class="text-n-slate-4/30"
            />
            <circle
              cx="21"
              cy="21"
              r="15.9"
              fill="none"
              :stroke="sat.color"
              stroke-width="4"
              :stroke-dasharray="`${sat.pct} ${100 - sat.pct}`"
              stroke-linecap="round"
              transform="rotate(-90 21 21)"
            />
          </svg>
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-xl font-black text-n-slate-12">{{
              sat.val
            }}</span>
          </div>
        </div>
        <div
          class="text-xs uppercase tracking-wider font-black text-n-slate-11"
        >
          {{ sat.nm }}
        </div>
        <p class="text-xs text-n-slate-11 mt-1.5 font-medium leading-relaxed">
          {{ sat.desc }}
        </p>
      </div>
    </div>

    <!-- Tópicos / assuntos -->
    <div
      class="bg-n-surface-1 border border-n-weak rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <h3 class="font-black text-n-slate-12 mb-1">
        {{ t('CHAT_DASHBOARD.TOPICS_TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ t('CHAT_DASHBOARD.TOPICS_CAP') }}
      </p>
      <div v-if="topics.length" class="flex flex-col divide-y divide-n-weak/50">
        <div
          v-for="(topic, ti) in topics"
          :key="topic.nm"
          class="flex items-center gap-4 py-3"
        >
          <span
            class="font-mono text-sm font-black text-n-teal-9 w-6 flex-shrink-0"
            >{{ String(ti + 1).padStart(2, '0') }}</span
          >
          <span class="flex-1 text-sm font-bold text-n-slate-12 min-w-0">{{
            topic.nm
          }}</span>
          <div
            class="flex-[2] h-2 bg-n-slate-4/30 rounded-full overflow-hidden"
          >
            <div
              class="h-full rounded-full bg-gradient-to-r from-n-teal-9 to-n-blue-9"
              :style="{ width: `${(topic.ct / topicMax) * 100}%` }"
            />
          </div>
          <span
            class="font-black text-n-slate-12 tabular-nums w-12 text-right"
            >{{ fmt(topic.ct) }}</span
          >
        </div>
      </div>
      <p v-else class="text-xs text-n-slate-11 italic">
        {{ t('CHAT_DASHBOARD.TOPICS_EMPTY') }}
      </p>
    </div>

    <!-- Rodapé -->
    <div
      class="flex justify-between text-xs text-n-slate-11 font-medium pt-4 border-t border-n-weak"
    >
      <span>{{ t('CHAT_DASHBOARD.FOOTER_NOTE') }}</span>
      <span>{{ t('CHAT_DASHBOARD.FOOTER_VERSION') }}</span>
    </div>
  </div>
</template>
